import {
  AS_PRICE_PRECID_C,
  AS_PRICE_SCHEMA_NAME,
  as400RecordFilterAllows,
  catalogRecordKeyScopeActive,
  type CatalogAdvancedFilter,
  type CatalogFilterMatch,
  type CatalogRecordKeyScope,
  type ProductRawDataRecord,
} from '@erp/rpc';

export { AS_PRICE_PRECID_C, AS_PRICE_SCHEMA_NAME };

/** Stable empty set, so an absent filter list needs no allocation. */
export const NO_ADVANCED_FILTERS: CatalogAdvancedFilter[] = [];

function rawCell(row: Record<string, unknown>, column: string): unknown {
  if (column in row) return row[column];
  const upper = column.toUpperCase();
  if (upper in row) return row[upper];
  const lower = column.toLowerCase();
  if (lower in row) return row[lower];
  for (const key of Object.keys(row)) {
    if (key.toLowerCase() === lower) return row[key];
  }
  return undefined;
}

/**
 * Ingest cells are `unknown`. Only primitives stringify to a PRICE RECORD ID;
 * objects would otherwise become `[object Object]` and never match `C` anyway.
 */
function rawCellText(row: Record<string, unknown>, column: string): string {
  const value = rawCell(row, column);
  if (typeof value === 'string') return value;
  if (typeof value === 'number') return String(value);
  return '';
}

/** Whether a linked record is an as_price row whose PRICE RECORD ID is C. */
export function isAsPricePrecidCRecord(record: ProductRawDataRecord): boolean {
  if (record.schemaDefinitionName.toLowerCase() !== AS_PRICE_SCHEMA_NAME) {
    return false;
  }
  if (record.row === null) return false;
  return as400RecordFilterAllows(
    record.schemaDefinitionName,
    rawCellText(record.row, 'precid'),
  );
}

/**
 * What ITEMMAST5 does to linked records: keep only as_price rows whose
 * `precid` is C, and leave every other schema alone (`as_item`,
 * `as_purmasth`, `as_prsls` — which also has `precid` — stay).
 */
export function narrowAsPriceToPrecidC(
  records: readonly ProductRawDataRecord[],
): ProductRawDataRecord[] {
  return records.filter((record) => {
    if (record.schemaDefinitionName.toLowerCase() !== AS_PRICE_SCHEMA_NAME) {
      return true;
    }
    return isAsPricePrecidCRecord(record);
  });
}

/**
 * What the expanded catalog row shows under its raw-data section: which
 * linked records survive ITEMMAST5, which metadata filters actually narrowed
 * them, and whether the section should say it was narrowed at all.
 *
 * Lives here so `ExpandedItemRow` stays a fetch + skeleton + pass-through
 * (its CRAP score trips the Fallow new-only gate when this branching stays
 * inline).
 */
export function expandedRowRawSection({
  records,
  filterMatch,
  metadataFilters,
  itemMast5Active,
  keyScope,
}: {
  records: readonly ProductRawDataRecord[];
  filterMatch: CatalogFilterMatch;
  metadataFilters: CatalogAdvancedFilter[];
  itemMast5Active: boolean;
  keyScope: CatalogRecordKeyScope;
}): {
  visibleRecords: ProductRawDataRecord[];
  narrowingFilters: CatalogAdvancedFilter[];
  narrowed: boolean;
} {
  // Under product-level matching the metadata filters didn't narrow these
  // records (the server returns every linked one), so they play no part in
  // the section's "narrowed" story either.
  const narrowingFilters = filterMatch === 'row' ? metadataFilters : [];
  const visibleRecords = itemMast5Active
    ? narrowAsPriceToPrecidC(records)
    : [...records];
  return {
    visibleRecords,
    narrowingFilters,
    narrowed:
      itemMast5Active ||
      narrowingFilters.length > 0 ||
      catalogRecordKeyScopeActive(keyScope),
  };
}

/**
 * Whether two advanced-filter lists select the same rows.
 *
 * Both the panel and the page compare an edited set against the applied one —
 * the panel to decide whether an edit is worth reporting, the page to tell a
 * filter still inside its debounce window from one already in the query. Field
 * comparison rather than serializing: it can't be thrown off by key order, and
 * it allocates nothing on the paths that run per render.
 */
export function sameAdvancedFilters(
  a: CatalogAdvancedFilter[],
  b: CatalogAdvancedFilter[],
): boolean {
  return (
    a.length === b.length &&
    a.every(
      (filter, i) =>
        filter.column === b[i].column &&
        filter.operator === b[i].operator &&
        filter.value === b[i].value,
    )
  );
}
